import type { Scene, Resolution, SceneSource } from '../types/obs';
import { SMETA, uuid, calcCrop } from './obs-engine';

const PV = 536936448;
const CU = '6c69626f-6273-4c00-9d88-c5136d61696e';

// Convert "#RRGGBB" to OBS's ABGR uint32 used by color_source
function hexToObsColor(hex: string): number {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0xff << 24 | b << 16 | g << 8 | r) >>> 0;
}

interface SourceRegistration {
  uuid: string;
  type: string;
}

interface AudioDevice {
  prev_ver: number;
  name: string;
  uuid: string;
  id: string;
  versioned_id: string;
  settings: { device_id: string };
  mixers: number;
  sync: number;
  flags: number;
  volume: number;
  balance: number;
  enabled: boolean;
  muted: boolean;
  'push-to-mute': boolean;
  'push-to-mute-delay': number;
  'push-to-talk': boolean;
  'push-to-talk-delay': number;
  hotkeys: Record<string, unknown[]>;
  deinterlace_mode: number;
  deinterlace_field_order: number;
  monitoring_type: number;
  private_settings: Record<string, unknown>;
}

function createAudioDevice(name: string, id: string): AudioDevice {
  return {
    prev_ver: PV,
    name,
    uuid: uuid(),
    id,
    versioned_id: id,
    settings: { device_id: 'default' },
    mixers: 255,
    sync: 0,
    flags: 0,
    volume: 1,
    balance: 0.5,
    enabled: true,
    muted: false,
    'push-to-mute': false,
    'push-to-mute-delay': 0,
    'push-to-talk': false,
    'push-to-talk-delay': 0,
    hotkeys: { 'libobs.mute': [], 'libobs.unmute': [], 'libobs.push-to-mute': [], 'libobs.push-to-talk': [] },
    deinterlace_mode: 0,
    deinterlace_field_order: 0,
    monitoring_type: 0,
    private_settings: {}
  };
}

function getCanonicalName(source: SceneSource, cameraIndex: number, screenIndex: number): string {
  // If the user has explicitly changed the name (i.e. it's not the default slot name),
  // we should keep their customized name!
  if (source.name !== source.slot) {
    return source.name;
  }

  const type = source.type;
  const nameLower = source.slot.toLowerCase();

  // 1. Camera sources (dshow_input)
  if (type === 'dshow_input') {
    if (cameraIndex === 0) return 'Camera';
    return `Camera ${cameraIndex + 1}`;
  }

  // 2. Screen sources (monitor_capture, window_capture, game_capture)
  if (type === 'monitor_capture' || type === 'window_capture' || type === 'game_capture') {
    if (screenIndex === 0) return 'Screen';
    return `Screen ${screenIndex + 1}`;
  }

  // 3. Color sources (color_source)
  if (type === 'color_source') {
    if (nameLower.includes('bg') || nameLower.includes('background')) {
      return 'Background Color';
    }
    if (nameLower.includes('border')) {
      return 'Border Color';
    }
  }

  // 4. Fallback: return the original name
  return source.name;
}

export function buildOBSProfile(
  scenes: Scene[],
  profileName: string,
  resolution: Resolution
): string {
  const cw = resolution.w;
  const ch = resolution.h;

  // Process and canonicalize source names per scene
  const processedScenes = scenes.map((sc) => {
    const sorted = [...sc.sources].sort((a, b) => a.order - b.order);
    let cameraCount = 0;
    let screenCount = 0;

    const mappedSources = sorted.map((s) => {
      let cameraIndex = -1;
      let screenIndex = -1;

      if (s.type === 'dshow_input') {
        cameraIndex = cameraCount++;
      } else if (s.type === 'monitor_capture' || s.type === 'window_capture' || s.type === 'game_capture') {
        screenIndex = screenCount++;
      }

      const canonicalName = getCanonicalName(s, cameraIndex, screenIndex);
      return {
        ...s,
        resolvedName: canonicalName
      };
    });

    return {
      ...sc,
      sources: mappedSources
    };
  });

  // Register unique sources
  const reg: Record<string, SourceRegistration> = {};
  // Border color_source registrations: keyed by "color|name" to keep them per-source
  const borderReg: Record<string, { uuid: string; color: number; name: string }> = {};
  processedScenes.forEach((sc) => {
    sc.sources.forEach((s) => {
      if (!reg[s.resolvedName]) {
        reg[s.resolvedName] = { uuid: uuid(), type: s.type };
      }
      if (s.border?.enabled && s.border.thickness > 0) {
        const bname = `${s.resolvedName} Border`;
        if (!borderReg[bname]) {
          borderReg[bname] = { uuid: uuid(), color: hexToObsColor(s.border.color), name: bname };
        }
      }
    });
  });

  // Build border color_source globals
  const borderGlobals = Object.values(borderReg).map((b) => ({
    prev_ver: PV,
    name: b.name,
    uuid: b.uuid,
    id: 'color_source',
    versioned_id: 'color_source_v3',
    settings: { color: b.color, width: 0, height: 0 },
    mixers: 0,
    sync: 0,
    flags: 0,
    volume: 1,
    balance: 0.5,
    enabled: true,
    muted: false,
    'push-to-mute': false,
    'push-to-mute-delay': 0,
    'push-to-talk': false,
    'push-to-talk-delay': 0,
    hotkeys: {},
    deinterlace_mode: 0,
    deinterlace_field_order: 0,
    monitoring_type: 0,
    private_settings: {},
  }));

  // Build global sources array
  const globals = Object.keys(reg).map((n) => {
    const e = reg[n];
    const m = SMETA[e.type] || {};
    return {
      prev_ver: PV,
      name: n,
      uuid: e.uuid,
      id: e.type,
      versioned_id: m.vid || e.type,
      settings: m.settings ? JSON.parse(JSON.stringify(m.settings)) : {},
      mixers: m.mix != null ? m.mix : 0,
      sync: 0,
      flags: 0,
      volume: 1,
      balance: 0.5,
      enabled: true,
      muted: false,
      'push-to-mute': false,
      'push-to-mute-delay': 0,
      'push-to-talk': false,
      'push-to-talk-delay': 0,
      hotkeys: m.hk ? JSON.parse(JSON.stringify(m.hk)) : {},
      deinterlace_mode: 0,
      deinterlace_field_order: 0,
      monitoring_type: 0,
      private_settings: {}
    };
  });

  // Build scene objects
  const scObjs = processedScenes.map((sc) => {
    const su = uuid();
    const sorted = sc.sources; // already sorted and mapped

    // Count total items including borders so hotkey ids stay in sync
    const totalItemCount = sorted.reduce(
      (acc, s) => acc + 1 + (s.border?.enabled && s.border.thickness > 0 ? 1 : 0),
      0
    );
    // Build hotkeys
    const hk: Record<string, unknown[]> = { 'OBSBasic.SelectScene': [] };
    for (let i = 0; i < totalItemCount; i++) {
      hk[`libobs.show_scene_item.${i + 1}`] = [];
      hk[`libobs.hide_scene_item.${i + 1}`] = [];
    }

    // Build scene items. Each source may emit a trailing border color_source item,
    // which is placed AFTER the source so OBS renders it behind (later items in
    // the items array sit below earlier items in the source list).
    const items: Record<string, unknown>[] = [];
    let idCounter = 0;
    sorted.forEach((s) => {
      const px = Math.round(s.position.x * cw);
      const py = Math.round(s.position.y * ch);
      const bw = Math.round(s.size.w * cw);
      const bh = Math.round(s.size.h * ch);
      const c = calcCrop(s.type, bw, bh, cw, ch);

      idCounter++;
      items.push({
        name: s.resolvedName,
        source_uuid: (reg[s.resolvedName] || { uuid: uuid() }).uuid,
        visible: s.visible !== false,
        locked: false,
        rot: 0,
        scale_ref: { x: 1, y: 1 },
        align: 5,
        id: idCounter,
        group_item_backup: false,
        pos: { x: px, y: py },
        scale_filter: 'disable',
        blend_method: 'default',
        blend_type: 'normal',
        show_transition: { duration: 300 },
        hide_transition: { duration: 300 },
        private_settings: {},
        bounds_type: 2,
        bounds_align: 0,
        bounds_crop: false,
        crop_left: c.l,
        crop_top: c.t,
        crop_right: c.r,
        crop_bottom: c.b,
        scale: { x: 1, y: 1 },
        bounds: { x: bw, y: bh },
      });

      if (s.border?.enabled && s.border.thickness > 0) {
        const bname = `${s.resolvedName} Border`;
        const b = borderReg[bname];
        if (b) {
          const t = Math.round(s.border.thickness);
          idCounter++;
          items.push({
            name: bname,
            source_uuid: b.uuid,
            visible: s.visible !== false,
            locked: false,
            rot: 0,
            scale_ref: { x: 1, y: 1 },
            align: 5,
            id: idCounter,
            group_item_backup: false,
            pos: { x: px - t, y: py - t },
            scale_filter: 'disable',
            blend_method: 'default',
            blend_type: 'normal',
            show_transition: { duration: 300 },
            hide_transition: { duration: 300 },
            private_settings: {},
            bounds_type: 2,
            bounds_align: 0,
            bounds_crop: false,
            crop_left: 0,
            crop_top: 0,
            crop_right: 0,
            crop_bottom: 0,
            scale: { x: 1, y: 1 },
            bounds: { x: bw + 2 * t, y: bh + 2 * t },
          });
        }
      }
    });

    return {
      prev_ver: PV,
      name: sc.name,
      uuid: su,
      id: 'scene',
      versioned_id: 'scene',
      settings: {
        custom_size: false,
        id_counter: items.length,
        items
      },
      mixers: 0,
      sync: 0,
      flags: 0,
      volume: 1,
      balance: 0.5,
      enabled: true,
      muted: false,
      'push-to-mute': false,
      'push-to-mute-delay': 0,
      'push-to-talk': false,
      'push-to-talk-delay': 0,
      hotkeys: hk,
      deinterlace_mode: 0,
      deinterlace_field_order: 0,
      monitoring_type: 0,
      canvas_uuid: CU,
      private_settings: {}
    };
  });

  // Build quick transitions
  const quickTransitions = [
    { name: 'Cut', duration: 300, hotkeys: [], id: 1, fade_to_black: false },
    { name: 'Fade', duration: 300, hotkeys: [], id: 2, fade_to_black: false },
    { name: 'Fade', duration: 300, hotkeys: [], id: 3, fade_to_black: true }
  ];

  // Build final profile
  const profile = {
    name: profileName,
    DesktopAudioDevice1: createAudioDevice('Desktop Audio', 'wasapi_output_capture'),
    AuxAudioDevice1: createAudioDevice('Mic/Aux', 'wasapi_input_capture'),
    groups: [],
    scene_order: scenes.map((s) => ({ name: s.name })),
    current_scene: scenes[0] ? scenes[0].name : '',
    current_program_scene: scenes[0] ? scenes[0].name : '',
    canvases: [],
    current_transition: 'Fade',
    transition_duration: 300,
    transitions: [],
    quick_transitions: quickTransitions,
    saved_projectors: [],
    preview_locked: false,
    scaling_enabled: false,
    scaling_level: -10,
    scaling_off_x: 0,
    scaling_off_y: 0,
    'virtual-camera': { type2: 3 },
    modules: {
      'auto-scene-switcher': {
        interval: 300,
        non_matching_scene: '',
        switch_if_not_matching: false,
        active: false,
        switches: []
      },
      captions: {
        source: '',
        enabled: false,
        lang_id: 1033,
        provider: 'mssapi'
      },
      'output-timer': {
        streamTimerHours: 0,
        streamTimerMinutes: 0,
        streamTimerSeconds: 30,
        recordTimerHours: 0,
        recordTimerMinutes: 0,
        recordTimerSeconds: 30,
        autoStartStreamTimer: false,
        autoStartRecordTimer: false,
        pauseRecordTimer: true
      },
      'scripts-tool': []
    },
    resolution: { x: cw, y: ch },
    version: 2,
    sources: globals.concat(borderGlobals).concat(scObjs)
  };

  return JSON.stringify(profile, null, 2);
}

export function downloadJSON(json: string, filename: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}
